import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { dataSelector, PermissionsLicensesLoader } from '@nextiva/global-store';

import { startPendo } from 'utils/pendo';
import { startDataDog } from 'utils/datadog';
import { http } from 'api/taxIdService';
import { accountV2Http } from 'services/accountV2/accountV2Service';
import { authHttp } from 'lib/services/http';
import {
  smsApiClient,
  tosApiClient,
  bulkImportApiClient,
  pnosApiClient,
} from 'services';
import { onboardingApiClient } from 'services/OOS/CampaignRegistryService';
import { useGetUserLicensesWithAttributes } from 'hooks/useGetUserLicensesWithAttributes/useGetUserLicensesWithAttributes';
import { getNextivaCXFreeTrialAttributes } from 'utils/getNextivaCXFreeTrialAttributes';
import {
  findFeatureFromLicenseList,
  getLicenseFeatures,
} from 'utils/license-helpers';
import { ssoClientId as clientId } from '../../ssoConfig';

export const Auth = ({ children }) => {
  const globalStoreData = useSelector(dataSelector);

  const {
    data: userLicensesWithAttributes,
    isLoading: isUserLicensesV3Loading,
  } = useGetUserLicensesWithAttributes();
  const nextivaCXFreeTrialAttributes = getNextivaCXFreeTrialAttributes(
    userLicensesWithAttributes?.data,
  );

  useEffect(() => {
    if (globalStoreData.isLoading === false && !isUserLicensesV3Loading) {
      const skipAdministration = findFeatureFromLicenseList(
        globalStoreData?.licenses,
        'SKIP_ADMINISTRATION',
      );
      const hasCXChatFeature = findFeatureFromLicenseList(
        globalStoreData?.licenses,
        'CX_CHAT',
      );

      const licenseFeatures = getLicenseFeatures(globalStoreData?.licenses);

      let userRole = 'User';
      if (
        globalStoreData?.roles[0]?.roleType === 'ROLE' &&
        globalStoreData?.roles[0]?.userDefined
      ) {
        userRole = 'User+';
      } else if (globalStoreData?.roles[0]?.roleType === 'SUPER_ADMIN') {
        userRole = 'Admin';
      }
      startPendo(
        globalStoreData.user,
        userRole,
        nextivaCXFreeTrialAttributes,
        skipAdministration,
        hasCXChatFeature,
        licenseFeatures,
      );
      startDataDog();
    }
  }, [
    globalStoreData.isLoading,
    globalStoreData.user,
    nextivaCXFreeTrialAttributes,
  ]);

  useEffect(() => {
    let httpInterceptor;
    let accountV2HttpInterceptor;
    let authHttpInterceptor;
    let pnosInterceptor;
    let onboardingInterceptor;
    let tosInterceptor;
    let smsInterceptor;
    let bulkImportInterceptor;
    if (globalStoreData.sso) {
      const interceptor = (config) => {
        config.headers.Authorization = `Bearer ${globalStoreData.sso}`;
        return config;
      };
      httpInterceptor = http.interceptors.request.use(interceptor);
      accountV2HttpInterceptor = accountV2Http.interceptors.request.use(
        interceptor,
      );
      authHttpInterceptor = authHttp.interceptors.request.use(interceptor);
      pnosInterceptor = pnosApiClient.interceptors.request.use(interceptor);
      onboardingInterceptor = onboardingApiClient.interceptors.request.use(
        interceptor,
      );
      tosInterceptor = tosApiClient.interceptors.request.use(interceptor);
      smsInterceptor = smsApiClient.interceptors.request.use(interceptor);
      bulkImportInterceptor = bulkImportApiClient.interceptors.request.use(
        interceptor,
      );
    }
    return () => {
      http.interceptors.request.eject(httpInterceptor);
      accountV2Http.interceptors.request.eject(accountV2HttpInterceptor);
      authHttp.interceptors.request.eject(authHttpInterceptor);
      pnosApiClient.interceptors.request.eject(pnosInterceptor);
      onboardingApiClient.interceptors.request.eject(onboardingInterceptor);
      tosApiClient.interceptors.request.eject(tosInterceptor);
      smsApiClient.interceptors.request.eject(smsInterceptor);
      bulkImportApiClient.interceptors.request.eject(bulkImportInterceptor);
    };
  }, [globalStoreData.sso]);

  return (
    // PermissionsLicenseLoader is used to load all the global-store data
    <PermissionsLicensesLoader
      ssoClientId={clientId}
      userId={encodeURIComponent(process.env?.REACT_APP_SSO_USERID)}
    >
      {/* PermissionsLicenseLoader requires that `children` get passed as a function */}
      {() => {
        return children;
      }}
    </PermissionsLicensesLoader>
  );
};

export default Auth;
